import { FC, useRef, useState, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import Button, { ButtonSize, ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import IconButton from '@zvuk-b2b/react-uikit/ui/IconButton';
import Radio from '@zvuk-b2b/react-uikit/ui/Radio';
import IconChevronDown from '@zvuk-b2b/react-uikit/ui/icons/ChevronDown';
import IconChevronUp from '@zvuk-b2b/react-uikit/ui/icons/ChevronUp';
import IconX from '@zvuk-b2b/react-uikit/ui/icons/X';
import Dropdown, { DropdownItemDataType } from '@zvuk-b2b/react-uikit/ui/Dropdown';
import ToolkitUtils from '@zvuk-b2b/react-uikit/toolkit/utils';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';

import { usePlayerContext } from 'application/providers/PlayerProvider';
import { PlayButton } from 'application/components/PlayButton';
import MediaListItemProgressSlider from 'application/components/MediaList/MediaListItem/MediaListItemProgressSlider';
import FileUtils from 'application/utils/FileUtils';
import { PromoRollSynthBackgroundType } from '@entities/PromoRollSynthJob/model';

import { TEXTS } from '../PromoRollSynthModal/texts';
import styles from './BackgroundSelect.module.scss';

type Props = {
  value: string;
  options: PromoRollSynthBackgroundType[];
  onChange: (id: string) => void;
};

const SAMPLE_ID_PREFIX = 'bg_sample';

const BackgroundSelect: FC<Props> = ({ value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { play, pause, resume, isPaused, isLoading, fileUuid, progress, changeProgress } = usePlayerContext();
  const { isMobile } = useMedia();

  const selectedOption = options.find((b) => b.backgroundId === value);

  const handleSampleClick = (bg: PromoRollSynthBackgroundType) => {
    const sampleId = `${SAMPLE_ID_PREFIX}_${bg.id}`;

    if (fileUuid === sampleId && !isPaused) {
      pause();
    } else if (fileUuid === sampleId && isPaused) {
      resume();
    } else {
      play({ fileUuid: sampleId, fileName: bg.title, fileUrl: FileUtils.generateMediafileURL(bg.fileUrl) });
    }
  };

  const handleSelect = (bg: PromoRollSynthBackgroundType) => {
    onChange(bg.backgroundId);
    setIsOpen(false);
  };

  const handleSampleKeyDown = (e: KeyboardEvent, bg: PromoRollSynthBackgroundType) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
      handleSampleClick(bg);
    }
  };

  const items: DropdownItemDataType[] = options.map((bg) => {
    const sampleId = `${SAMPLE_ID_PREFIX}_${bg.id}`;
    const isSampleActive = fileUuid === sampleId;
    const isPlaying = isSampleActive && !isPaused;

    return {
      key: bg.id,
      value: bg.title,
      selected: value === bg.backgroundId,
      onClick: () => handleSelect(bg),
      content: (
        <>
          <Radio
            elementId={`bg_radio_${bg.id}`}
            isChecked={value === bg.backgroundId}
            onChange={() => handleSelect(bg)}
          />

          <div
            role="button"
            tabIndex={0}
            data-testid={`bg_sample_${bg.id}`}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              handleSampleClick(bg);
            }}
            onKeyDown={(e) => handleSampleKeyDown(e, bg)}>
            <PlayButton
              isMediafilePlaying={isPlaying}
              isMediaFilePaused={isSampleActive && isPaused}
              isLoading={isSampleActive && isLoading}
            />
          </div>

          <span className={styles.optionLabel}>{bg.title}</span>

          {isSampleActive && (
            <MediaListItemProgressSlider
              elementId={`bg_progress_${bg.id}`}
              progress={progress}
              onChange={changeProgress}
            />
          )}
        </>
      ),
    };
  });

  return (
    <div
      className={styles.container}
      ref={containerRef}>
      <span className={styles.label}>{TEXTS.FIELD_BACKGROUND_LABEL}</span>

      <Button
        ref={triggerRef}
        elementId="background_select_trigger"
        type={ButtonType.SECONDARY}
        size={ButtonSize.MEDIUM}
        className={classNames(styles.trigger, { [styles.triggerOpen]: isOpen })}
        onClick={() => setIsOpen((v) => !v)}>
        <span className={classNames({ [styles.placeholder]: !selectedOption })}>
          {selectedOption?.title ?? TEXTS.FIELD_BACKGROUND_PLACEHOLDER}
        </span>
        {isOpen ? <IconChevronUp /> : <IconChevronDown />}
      </Button>

      {isOpen &&
        (() => {
          const dropdown = (
            <>
              {isMobile && (
                <IconButton
                  elementId="background_select_dropdown_close_button"
                  className={styles.dropdownCloseButton}
                  onClick={(event) => {
                    setIsOpen(false);
                    event.preventDefault();
                  }}>
                  <IconX />
                </IconButton>
              )}

              <Dropdown
                elementId="background_select_dropdown"
                className={styles.dropdown}
                itemContentClassName={styles.optionContent}
                items={items}
                anchorNode={triggerRef.current!}
                parentNode={containerRef.current!}
                style={isMobile ? undefined : { width: ToolkitUtils.getWidth(triggerRef.current!) }}
                onClose={() => setIsOpen(false)}
                isFullMode={isMobile}
                withoutWrapper={isMobile}
                isNeedCloseWhileParentScrolling={!isMobile}
              />
            </>
          );

          return isMobile ? createPortal(dropdown, document.body) : dropdown;
        })()}
    </div>
  );
};

export default BackgroundSelect;
